import React, { useEffect, useState } from 'react';
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import apiGlobal from 'global/api.global';
import {
    Button, Card, CardBody, CardFooter, CardHeader, Col, Form, Row
} from 'reactstrap';
import {
    DO_ENGG_ReportingStatus,
    VesselTypeConstant
} from 'shared/constants';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import { getAuthData, loggedInUser } from 'utils/auth/authUtils';
import { useFormik } from 'formik';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { queryKeyes } from 'shared/queryKeys';
import { queryClient } from 'react-query/queryClient';
import {
    checkValidObject,
    handleRemarksPreviousButton,
    handleServerResponse,
    useSafeFormikContext
} from 'GenericForms/Helper';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import FormLoading from 'Components/FormLoading';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';
import ReactQuill from 'react-quill';

interface RemarksType {
    handleVesselState: any;
    toggleDynamicTabs: any;
    setErrorMessage: any;
    toggleAddLfondsTab: (value: number) => void;
    toggleAddEconTab: (value: number) => void;
}

const Remarks = ({
    handleVesselState,
    toggleDynamicTabs,
    setErrorMessage,
    toggleAddLfondsTab,
    toggleAddEconTab,
}: RemarksType) => {
    const { setSaveOrSubmit, editReportBool, setDiscardwarning, setEnggActiveTabId, previousEnggTabIndex } = useEditReport();
    /** State variables */
    useDocumentTitle("Remarks - ecoSAIL");
    const { ReportID, VesselID, VoyageID, CurrentVessel, CurrentReport, loading } = useSelector((state: RootState) => state.Reporting);
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const engineerRemarksCurrentReport = CurrentReport?.data?.chief_engineer?.engineer_remarks ?? null;
    const isLNG = CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER && CurrentVessel?.display;
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const responseArray: any[] = [];
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const [saveOrCompleate, setSaveOrComplete] = useState<'save' | 'complete'>('save');
    /** State variables end */

    const getInitialValues = () => {
        if (checkValidObject(editInitialValues?.chief_engineer?.engineer_remarks)) {
            return editInitialValues?.chief_engineer?.engineer_remarks;
        } else if (checkValidObject(engineerRemarksCurrentReport)) {
            return engineerRemarksCurrentReport;
        } else {
            return {
                remarks: "",
                user: loggedInUser(),
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            };
        }
    };

    const saveRemark = async (remark: any) => {
        /** Remarks' submit request */
        if (checkValidObject(engineerRemarksCurrentReport)) {
            responseArray.push(apiGlobal.put(`/engineer_remark/${remark.id}/`, remark))
        } else {
            responseArray.push(apiGlobal.post(`/engineer_remark/`, remark))
        }
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                setErrorMessage(null)
                await queryClient.invalidateQueries(queryKeyes.vessel.EngineerRemark.key);
                setRefreshKey(refreshKey + 1)
            } else {
                setErrorMessage(res)
            }
        });
    };

    const handleSaveandComplete = async (remark: any) => {
        if (remark.remarks) {
            await saveRemark(remark);
        }
        responseArray.push(apiGlobal.patch(`/vessel_reporting_information/${ReportID}/`, {
            engineer_reporting_status: DO_ENGG_ReportingStatus.COMPLETE
        }));
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                setErrorMessage(null);
                await queryClient.invalidateQueries(queryKeyes.vessel.EngineerRemark.key);
                setRefreshKey(prev => prev + 1);
                setEnggActiveTabId(1);
                handleVesselState('ONGOING_VESSEL_REPORTING');
            } else {
                setErrorMessage(res);
            }
        });
    };

    const EnggRemarksFormik = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: async (values, actions) => {
            actions.setSubmitting(true);
            if (values.remarks) {
                await saveRemark(values);
            }
            actions.setSubmitting(false);
        }
    });

    useEffect(() => {
        if (editValues && editValues?.chief_engineer?.engineer_remarks) {
            setEditValues({
                ...editValues,
                chief_engineer: {
                    ...editValues?.chief_engineer,
                    engineer_remarks: {
                        ...EnggRemarksFormik?.values,
                        report_index: null,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                        user: getAuthData('user_id'),
                        vessel: VesselID
                    }
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [EnggRemarksFormik?.values])

    return (
        <Card>
            <CardHeader className='p-2 pb-0'>
                <div className="text-center mb-3">
                    <Row>
                        <Col>
                            <h5 className="page_title pos-start mb-0">Remarks (if any)</h5>
                        </Col>
                        <Col>
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {loading && <FormLoading />}
            {!loading && (
                <Form className="needs-validation" autoComplete="off" onSubmit={EnggRemarksFormik.handleSubmit} key={refreshKey}>
                    <CardBody className="engineer-card-body">
                        <Row>
                            <div>
                                <ReactQuill
                                    key={refreshKey}
                                    theme="snow"
                                    value={EnggRemarksFormik.values.remarks}
                                    onChange={(value: any) => {
                                        EnggRemarksFormik.setFieldValue('remarks', value);
                                    }}
                                    modules={{
                                        toolbar: [
                                            ['bold', 'italic', 'underline', 'strike'],
                                            [{ list: 'ordered' }, { list: 'bullet' }],
                                            [{ align: [] }],
                                            ['clean']
                                        ]
                                    }}
                                    className="custom-quill-editor table-light"
                                />
                            </div>
                        </Row>
                    </CardBody>
                    <CardFooter className="p-2 py-3">
                        <Row className="d-flex align-items-center flex-row-reverse">
                            <Col lg={10}>
                                <div className="d-flex flex-wrap gap-3 justify_right">
                                    {editReportBool ? (
                                        <>
                                            <Button
                                                type="button"
                                                color="danger"
                                                className="me-3"
                                                onClick={() => setDiscardwarning(true)}
                                            >
                                                Discard
                                            </Button>
                                            {/* <Button
                                                type="reset"
                                                color="danger"
                                                className="btn_size_cstm"
                                                onClick={() => {
                                                    setEditValues({
                                                        ...editValues,
                                                        chief_engineer: {
                                                            ...editValues.chief_engineer,
                                                            engineer_remarks: {
                                                                ...editInitialValues.chief_engineer.engineer_remarks,
                                                                report_index: null
                                                            }
                                                        }
                                                    });
                                                    EnggRemarksFormik?.resetForm();
                                                    setRefreshKey(prev => prev + 1);
                                                }}
                                            >
                                                Reset
                                            </Button> */}
                                            <Button
                                                type="button"
                                                color="primary"
                                                onClick={async () => {
                                                    setSaveOrSubmit(false);
                                                    setSaveOrComplete('save');
                                                    if (EnggRemarksFormik.isValid) {
                                                        await editSubmit()?.then(async () => {
                                                            await queryClient.invalidateQueries(queryKeyes.edit.getDraft.key);
                                                        }).catch(console.error);
                                                    }
                                                }}
                                                disabled={editIsSubmitting || EnggRemarksFormik?.isSubmitting}
                                            >
                                                {(editIsSubmitting && saveOrCompleate === 'save') && (
                                                    <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                                )}
                                                Save
                                            </Button>
                                            <Button
                                                type="button"
                                                color="primary"
                                                onClick={async () => {
                                                    setSaveOrSubmit(true);
                                                    setSaveOrComplete('complete');
                                                    if (EnggRemarksFormik.isValid) {
                                                        await editSubmit()?.then(async () => {
                                                            await queryClient.invalidateQueries(queryKeyes.edit.getDraft.key);
                                                        }).catch(console.error);
                                                    }
                                                }}
                                                disabled={editIsSubmitting}
                                            >
                                                {((editIsSubmitting && saveOrCompleate === 'complete') || EnggRemarksFormik?.isSubmitting) && (
                                                    <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                                )}
                                                Complete
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            {isLNG && (
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    className="btn_size_cstm"
                                                    disabled={EnggRemarksFormik?.isSubmitting}
                                                >
                                                    {EnggRemarksFormik?.isSubmitting && (
                                                        <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                                    )}Save
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                color="primary"
                                                className="btn_size_cstm"
                                                onClick={() => handleSaveandComplete(EnggRemarksFormik.values)}
                                                disabled={EnggRemarksFormik?.isSubmitting}
                                            >
                                                {EnggRemarksFormik?.isSubmitting && (
                                                    <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                                )}{isLNG ? 'Save & Complete' : 'Complete'}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </Col>
                            <Col lg={2}>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm"
                                    onClick={() => {
                                        setErrorMessage(null);
                                        EnggRemarksFormik.setErrors({});
                                        handleRemarksPreviousButton(
                                            currentVesselReport,
                                            toggleDynamicTabs,
                                            previousEnggTabIndex,
                                            toggleAddLfondsTab,
                                            toggleAddEconTab
                                        );
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </Col>
                        </Row>
                    </CardFooter>
                    <FormValuesDebug values={[EnggRemarksFormik.values, EnggRemarksFormik.errors, EnggRemarksFormik.initialValues]} />
                </Form>
            )}
        </Card>
    );
};

export default Remarks;